import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FOOTER_BREAKPOINT,
  MOBILE_BREAKPOINT,
  convertToVwMob,
  convertToVwWeb,
} from '../../Utils/responsive';
import mainLogo from '../../assets/Logo/main_logo.png';
import { MISSIONFUND_HOME_URL } from '../../Utils/missionFundLegalUrls';
import { PATHS } from '../../routes/paths';
import { useAppDispatch } from '../../store/hooks';
import { signOutAndClear } from '../../Module/Auth/Presentation/authSlice';

type Props = {
  title?: string;
  rightLinks?: React.ReactNode;
};

const defaultLinkKeys = ['myAccount', 'logout', 'support'] as const;

const NavBar: React.FC<Props> = ({ title, rightLinks }) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAccountPage =
    location.pathname === PATHS.MY_ACCOUNT ||
    location.pathname.startsWith(`${PATHS.MY_ACCOUNT}/`);

  const handleDefaultLinkClick = (key: (typeof defaultLinkKeys)[number]) => {
    if (key === 'logout') {
      dispatch(signOutAndClear());
      navigate(PATHS.LOGIN, { replace: true });
      return;
    }
    if (key === 'myAccount') {
      navigate(isAccountPage ? PATHS.PROJECT_MANAGEMENT : PATHS.MY_ACCOUNT);
      return;
    }
    if (key === 'support') {
      window.open('http://pf.kakao.com/_xkHknxd/chat', '_blank', 'noopener,noreferrer');
      return;
    }
  };

  // 기부계좌 신청 화면으로 이동
  // (기부계좌 연결 여부 API 가 나오면 신청한 계정은 '기부계좌 이동' — MISSIONFUND_MYACCOUNT_SSO_URL 로 바꾼다)
  const handleGivingAccountClick = () => {
    navigate(PATHS.GIVING_ACCOUNT_APPLY);
  };

  const projectDashboardTitle = t('common.layout.projectManagement');
  const titleNavigatesToProject = Boolean(title && title === projectDashboardTitle);

  return (
    <Bar>
      <Inner>
      <Left>
        <LogoLink
          href={MISSIONFUND_HOME_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('common.footer.logoAlt')}
        >
          <Logo src={mainLogo} alt="" />
        </LogoLink>
        <Divider />
        {title &&
          (titleNavigatesToProject ? (
            <TitleButton type="button" onClick={() => navigate(PATHS.PROJECT_MANAGEMENT)}>
              {title}
            </TitleButton>
          ) : (
            <Title>{title}</Title>
          ))}
      </Left>
      <Right>
        {rightLinks ? (
          rightLinks
        ) : (
          <>
            <GivingAccountButton type="button" onClick={handleGivingAccountClick}>
              {t('translation:nav.givingAccountApply')}
            </GivingAccountButton>
            {defaultLinkKeys.map((key) => (
              <NavLinkButton
                key={key}
                type="button"
                onClick={() => handleDefaultLinkClick(key)}
              >
                {key === 'myAccount' && isAccountPage
                  ? t('translation:common.layout.projectManagement')
                  : t(`translation:nav.${key}`)}
              </NavLinkButton>
            ))}
          </>
        )}
      </Right>
      </Inner>
    </Bar>
  );
};

const Bar = styled.header`
  height: ${convertToVwWeb(60)};
  background: ${({ theme }) => theme.colors.other.mainBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  position: sticky;
  top: 0;
  z-index: 10;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${convertToVwMob(56)};
  }
`;

// WebPageLayout의 Content(max-width: 1200 + padding 16/12)와 좌우 시작선 맞춤
const Inner = styled.div`
  height: 100%;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-sizing: border-box;

  @media (max-width: ${FOOTER_BREAKPOINT}px) {
    padding: 0 12px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 ${convertToVwMob(16)};
  }
`;

const Divider = styled.div`
  width: 1px;
  height: ${convertToVwWeb(28)};
  background: ${({ theme }) => theme.colors.grayScale[30]};
  flex-shrink: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${convertToVwMob(24)};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(14)};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: ${convertToVwMob(10)};
  }
`;

const LogoLink = styled.a`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
`;

const Logo = styled.img`
  height: ${convertToVwWeb(26)};
  object-fit: contain;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: ${convertToVwMob(22)};
  }
`;

const titleTextStyles = `
  font-size: ${convertToVwWeb(24)};
  font-weight: 600;
  color: #17171B;
  font-style: normal;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: ${convertToVwMob(15)};
  }
`;

const Title = styled.div`
  ${titleTextStyles}
`;

const TitleButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  ${titleTextStyles}

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[80]};
  }
`;

const Right = styled.nav`
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(18)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.medium20};


  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: ${convertToVwMob(10)};
    font-size: ${convertToVwMob(12)};
  }
`;

const NavLinkButton = styled.button<{ $clickable?: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;

  &:hover {
    color: ${({ theme, $clickable }) =>
      $clickable ? theme.colors.grayScale[90] : 'inherit'};
  }
`;

const GIVING_ACCOUNT_BUTTON_COLOR = '#2BC2E1';

const GivingAccountButton = styled.button`
  appearance: none;
  border: 1px solid ${GIVING_ACCOUNT_BUTTON_COLOR};
  border-radius: ${convertToVwWeb(8)};
  background: transparent;
  padding: ${convertToVwWeb(6)} ${convertToVwWeb(14)};
  color: ${GIVING_ACCOUNT_BUTTON_COLOR};
  font: inherit;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${GIVING_ACCOUNT_BUTTON_COLOR};
    color: #ffffff;
  }
`;

export default NavBar;

